import React, { useEffect } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { CampaignRequest } from '~/types/campaigns';
import StatusBadge from '~/components/CampaignRequestDetails/StatusBadge';

interface Props {
    campaigns: CampaignRequest[];
}

const columnHelper = createColumnHelper<CampaignRequest>();

const columns = [
    columnHelper.accessor(
        (row) => {
            const firstName = row.patient_first_name || row.initial_first_name || '';
            const lastName = row.patient_last_name || row.initial_last_name || '';
            return `${firstName} ${lastName}`.trim();
        },
        {
            id: 'clientName',
            header: () => 'Client',
            cell: (info) => (
                <>
                    <Link to={`/campaign-requests/${info.row.original.id}`}>
                        <div className='font-bold text-primary'>
                            {info.getValue() || 'No name'}
                        </div>
                    </Link>
                </>
            ),
        }
    ),
    columnHelper.accessor('status.name', {
        header: 'Status',
        cell: (info) => (
            <StatusBadge status={info?.getValue() || ''} />
        ),
    }),
    columnHelper.accessor('primary_medical_issue.name', {
        header: 'Medical Reason',
        cell: (info) => (
            <>
                <div>{info.renderValue()}</div>
            </>
        ),
    }),
];

const CampaignRequestTable: React.FC<Props> = ({ campaigns }) => {
    const [data, setData] = React.useState(() => [...campaigns]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    useEffect(() => {
        setData(campaigns);
    }, [campaigns]);

    return (
        <div className='overflow-x-auto'>
            <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                <thead className='border-separate px-4'>
                    {table?.getHeaderGroups()?.map((headerGroup) => (
                        <tr
                            className='border-t border-stroke bg-whiten'
                            style={{ fontSize: '14px' }}
                            key={headerGroup.id}
                        >
                            {headerGroup.headers.map((header) => (
                                <th
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                    }}
                                    key={header.id}
                                >
                                    <div className='flex items-center'>
                                        <span>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext()
                                                  )}
                                        </span>
                                        <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                            <span className='inline-block'>
                                                <svg
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    width='17'
                                                    height='16'
                                                    viewBox='0 0 17 16'
                                                    fill='none'
                                                >
                                                    <path
                                                        d='M8.5 14.6666L4.5 10.6666L5.45 9.71659L8.5 12.7666L11.55 9.71659L12.5 10.6666L8.5 14.6666ZM5.46667 6.26659L4.5 5.33325L8.5 1.33325L12.5 5.33325L11.5333 6.26659L8.5 3.23325L5.46667 6.26659Z'
                                                        fill='#131212'
                                                    />
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </th>
                            ))}
                            <th
                                style={{
                                    width: '70px',
                                    paddingTop: '16px',
                                    paddingBottom: '16px',
                                }}
                            ></th>
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table?.getRowModel()?.rows?.map((row) => (
                        <tr className='border-t border-stroke' key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <td
                                    style={{
                                        paddingTop: '16px',
                                        paddingBottom: '16px',
                                    }}
                                    key={cell.id}
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                            <td
                                style={{
                                    paddingTop: '16px',
                                    paddingBottom: '16px',
                                }}
                            >
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='24'
                                    height='24'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                >
                                    <path
                                        d='M12 20C11.45 20 10.9792 19.8042 10.5875 19.4125C10.1958 19.0208 10 18.55 10 18C10 17.45 10.1958 16.9792 10.5875 16.5875C10.9792 16.1958 11.45 16 12 16C12.55 16 13.0208 16.1958 13.4125 16.5875C13.8042 16.9792 14 17.45 14 18C14 18.55 13.8042 19.0208 13.4125 19.4125C13.0208 19.8042 12.55 20 12 20ZM12 14C11.45 14 10.9792 13.8042 10.5875 13.4125C10.1958 13.0208 10 12.55 10 12C10 11.45 10.1958 10.9792 10.5875 10.5875C10.9792 10.1958 11.45 10 12 10C12.55 10 13.0208 10.1958 13.4125 10.5875C13.8042 10.9792 14 11.45 14 12C14 12.55 13.8042 13.0208 13.4125 13.4125C13.0208 13.8042 12.55 14 12 14ZM12 8C11.45 8 10.9792 7.80417 10.5875 7.4125C10.1958 7.02083 10 6.55 10 6C10 5.45 10.1958 4.97917 10.5875 4.5875C10.9792 4.19583 11.45 4 12 4C12.55 4 13.0208 4.19583 13.4125 4.5875C13.8042 4.97917 14 5.45 14 6C14 6.55 13.8042 7.02083 13.4125 7.4125C13.0208 7.80417 12.55 8 12 8Z'
                                        fill='#00AEB3'
                                    />
                                </svg>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className='border-t border-stroke px-5 pt-5'>
                <p className='text-[12px]/[16px]'>Showing {data.length} results</p>
            </div>
        </div>
    );
};

export default CampaignRequestTable;
